import { useMemo } from "react";
import { motion } from "framer-motion";
import { formatarMoedaBrasileira } from "../../utils/formatarMoedaBrasileira";

// As metas vêm do snapshot dos parâmetros gravado junto com a comissão (o backend decide o
// pagamento com esses mesmos valores). Os números abaixo só são usados se o snapshot não existir.
const METAS_PADRAO = { conclusao: 80, greenmile: 95, telemetria: 98, retorno: 1 };

const lerMetas = (c) => {
    try {
        const s = c?.parametrosSnapshotJson ? JSON.parse(c.parametrosSnapshotJson) : null;
        return {
            conclusao: s?.percentualConclusao ?? METAS_PADRAO.conclusao,
            greenmile: s?.percentualGreenmile ?? METAS_PADRAO.greenmile,
            telemetria: s?.metaTelemetria ?? METAS_PADRAO.telemetria,
            retorno: s?.limiteMaximoPercentualRetorno ?? METAS_PADRAO.retorno,
        };
    } catch {
        return METAS_PADRAO;
    }
};

const derivarMetas = (comissoes) => lerMetas(comissoes.find(c => c.parametrosSnapshotJson));

const getPercentualConclusao = (c) => c.conclusaoRotaGreenmile?.percentualConclusao ?? c.percentualConclusao;
const getMediaTelemetria = (c) => c.telemetria?.mediaSegurancaViaria ?? c.mediaSegurancaViaria;
const getMetaTelemetria = (c) => c.telemetria?.meta ?? c.meta ?? lerMetas(c).telemetria;
const getPercentualRetorno = (c) => c.retorno?.percentualRetornoTotal ?? c.percentualRetornoTotal;

const atingiuConclusao = (c) => {
    // o backend já avalia conclusão (+ Green Mile para motorista) com os parâmetros usados no cálculo
    if (c.atingiuMetaConclusaoGreenmile != null && getPercentualConclusao(c) != null) {
        return c.atingiuMetaConclusaoGreenmile === "Atingiu";
    }
    const p = getPercentualConclusao(c);
    return p == null ? true : p >= lerMetas(c).conclusao;
};
const atingiuTelemetria = (c) => {
    const flag = c.telemetria?.atingiuMeta ?? c.atingiuMeta;
    if (flag != null) return flag;
    const media = getMediaTelemetria(c);
    return media == null ? true : media >= getMetaTelemetria(c);
};
const atingiuRetorno = (c) => {
    const ultrapassou = c.retorno?.ultrapassouLimite ?? c.ultrapassouLimite;
    if (ultrapassou != null) return !ultrapassou;
    const flag = c.retorno?.atingiuMetaRetorno ?? c.atingiuMetaRetorno;
    if (flag != null) return flag;
    const p = getPercentualRetorno(c);
    return p == null ? true : p <= lerMetas(c).retorno;
};

function DashboardTab({ comissoes, onFuncionarioSelected }) {
    const stats = useMemo(() => {
        if (!comissoes.length) return null;

        const total = comissoes.reduce((a, c) => a + (c.valorTotalReceber ?? 0), 0);
        const totalSemDesc = comissoes.reduce((a, c) => a + (c.totalSemDesconto ?? 0), 0);
        const totalDesc = comissoes.reduce((a, c) => a + (c.descontos ?? 0), 0);
        const mediaValor = total / comissoes.length;
        const ativos = comissoes.filter(c => c.funcionarioAtivo).length;

        const motoristas = comissoes.filter(c => c.funcionarioCargo?.toLowerCase().includes("motorista"));
        const auxiliares = comissoes.filter(c => c.funcionarioCargo?.toLowerCase().includes("auxiliar"));

        const porFilial = comissoes.reduce((acc, c) => {
            const f = c.filialNome ?? "Sem filial";
            if (!acc[f]) acc[f] = { total: 0, count: 0 };
            acc[f].total += c.valorTotalReceber ?? 0;
            acc[f].count++;
            return acc;
        }, {});

        const porCargo = comissoes.reduce((acc, c) => {
            const cargo = c.funcionarioCargo ?? "Outros";
            if (!acc[cargo]) acc[cargo] = { total: 0, count: 0 };
            acc[cargo].total += c.valorTotalReceber ?? 0;
            acc[cargo].count++;
            return acc;
        }, {});

        const maxFilialTotal = Math.max(...Object.values(porFilial).map(v => v.total));
        const maxCargoTotal = Math.max(...Object.values(porCargo).map(v => v.total));

        const top5 = [...comissoes]
            .sort((a, b) => (b.valorTotalReceber ?? 0) - (a.valorTotalReceber ?? 0))
            .slice(0, 5);

        const comConclusao = comissoes.filter(c => getPercentualConclusao(c) != null);
        const mediaMeta = comConclusao.length
            ? comConclusao.reduce((a, c) => a + getPercentualConclusao(c), 0) / comConclusao.length
            : 0;

        const atingiramMeta = comissoes.filter(c => atingiuTelemetria(c)).length;

        const naoAtingiuConclusao = comissoes
            .filter(c => !atingiuConclusao(c))
            .sort((a, b) => (getPercentualConclusao(a) ?? 0) - (getPercentualConclusao(b) ?? 0));

        const naoAtingiuTelemetria = comissoes
            .filter(c => !atingiuTelemetria(c))
            .sort((a, b) => (getMediaTelemetria(a) ?? 0) - (getMediaTelemetria(b) ?? 0));

        const naoAtingiuRetorno = comissoes
            .filter(c => !atingiuRetorno(c))
            .sort((a, b) => (getPercentualRetorno(b) ?? 0) - (getPercentualRetorno(a) ?? 0));

        return {
            total, totalSemDesc, totalDesc, mediaValor, ativos,
            motoristas: motoristas.length, auxiliares: auxiliares.length,
            porFilial, porCargo, maxFilialTotal, maxCargoTotal,
            top5, mediaMeta, atingiramMeta,
            naoAtingiuConclusao, naoAtingiuTelemetria, naoAtingiuRetorno,
            totalFuncionarios: comissoes.length,
        };
    }, [comissoes]);

    const metas = useMemo(() => derivarMetas(comissoes), [comissoes]);

    if (!comissoes.length) return (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <i className="fa fa-bar-chart text-5xl mb-4 opacity-30" />
            <p className="text-sm">Nenhum dado disponível. Selecione um período.</p>
        </div>
    );

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 space-y-5"
        >
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                <StatCard icon="fa fa-users" label="Funcionários" value={stats.totalFuncionarios} color="blue" />
                <StatCard icon="fa fa-truck" label="Motoristas" value={stats.motoristas} color="teal" />
                <StatCard icon="fa fa-user" label="Auxiliares" value={stats.auxiliares} color="violet" />
                <StatCard icon="fa fa-dollar" label="Total a Pagar" value={`R$ ${formatarMoedaBrasileira(stats.total)}`} color="teal" />
                <StatCard icon="fa fa-calculator" label="Média por Func." value={`R$ ${formatarMoedaBrasileira(stats.mediaValor)}`} color="amber" />
                <StatCard icon="fa fa-cut" label="Total Descontos" value={`R$ ${formatarMoedaBrasileira(stats.totalDesc)}`} color="rose" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">

                {/* Por Filial */}
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-foreground">
                        <i className="fa fa-building text-teal-600" /> Total por Filial
                    </h3>
                    <div className="space-y-2.5">
                        {Object.entries(stats.porFilial)
                            .sort((a, b) => b[1].total - a[1].total)
                            .map(([nome, { total, count }]) => (
                                <MiniBar
                                    key={nome}
                                    label={nome}
                                    value={total}
                                    max={stats.maxFilialTotal}
                                    suffix=""
                                />
                            ))}
                    </div>
                </div>

                {/* Por Cargo */}
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-foreground">
                        <i className="fa fa-id-badge text-blue-600" /> Total por Cargo
                    </h3>
                    <div className="space-y-2.5">
                        {Object.entries(stats.porCargo)
                            .sort((a, b) => b[1].total - a[1].total)
                            .map(([cargo, { total, count }]) => (
                                <MiniBar
                                    key={cargo}
                                    label={cargo}
                                    value={total}
                                    max={stats.maxCargoTotal}
                                    color="#3b82f6"
                                />
                            ))}
                    </div>
                </div>

                {/* Top 5 */}
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-foreground">
                        <i className="fa fa-trophy text-amber-500" /> Top 5 Maiores Comissões
                    </h3>
                    <div className="space-y-2">
                        {stats.top5.map((c, i) => (
                            <div key={c.funcionarioId} className="flex items-center gap-2 text-xs">
                                <span className={`w-5 h-5 rounded-full flex items-center justify-center font-bold text-white text-[10px] flex-shrink-0
                                    ${i === 0 ? "bg-amber-400" : i === 1 ? "bg-gray-400" : i === 2 ? "bg-orange-400" : "bg-muted text-muted-foreground"}`}>
                                    {i + 1}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <p className="truncate font-medium text-foreground">{c.funcionarioNome}</p>
                                    <p className="text-muted-foreground truncate">{c.filialNome}</p>
                                </div>
                                <span className="font-bold text-teal-700 flex-shrink-0">
                                    R$ {formatarMoedaBrasileira(c.valorTotalReceber)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* % Conclusão média */}
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-foreground">
                        <i className="fa fa-check-circle text-teal-600" /> Média de Conclusão de Rota
                    </h3>
                    <div className="flex flex-col items-center justify-center py-4">
                        <div className="relative w-28 h-28">
                            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                                <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" strokeWidth="10" />
                                <circle
                                    cx="50" cy="50" r="40" fill="none"
                                    stroke="#14b8a6" strokeWidth="10"
                                    strokeDasharray={`${2 * Math.PI * 40 * stats.mediaMeta / 100} ${2 * Math.PI * 40}`}
                                    strokeLinecap="round"
                                />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-xl font-bold text-teal-700">{stats.mediaMeta.toFixed(1)}%</span>
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">Média geral de conclusão</p>
                    </div>
                </div>

                {/* Distribuição sem desc / descontos / receber */}
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-foreground">
                        <i className="fa fa-pie-chart text-violet-600" /> Distribuição Financeira
                    </h3>
                    <div className="space-y-3 mt-2">
                        {[
                            { label: "Sem Desconto", value: stats.totalSemDesc, color: "#8b5cf6" },
                            { label: "Descontos", value: stats.totalDesc, color: "#f43f5e" },
                            { label: "A Receber", value: stats.total, color: "#14b8a6" },
                        ].map(item => (
                            <div key={item.label}>
                                <div className="flex justify-between text-xs mb-1">
                                    <span className="text-muted-foreground">{item.label}</span>
                                    <span className="font-semibold">R$ {formatarMoedaBrasileira(item.value)}</span>
                                </div>
                                <div className="bg-muted rounded-full h-2 overflow-hidden">
                                    <div
                                        className="h-2 rounded-full"
                                        style={{
                                            width: `${Math.min((item.value / stats.totalSemDesc) * 100, 100)}%`,
                                            background: item.color
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Resumo geral */}
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-foreground">
                        <i className="fa fa-info-circle text-blue-600" /> Resumo Geral
                    </h3>
                    <div className="space-y-2 text-xs">
                        {[
                            { label: "Total sem desconto", value: `R$ ${formatarMoedaBrasileira(stats.totalSemDesc)}` },
                            { label: "Total de descontos", value: `R$ ${formatarMoedaBrasileira(stats.totalDesc)}` },
                            { label: "Total a receber", value: `R$ ${formatarMoedaBrasileira(stats.total)}`, bold: true },
                            { label: "Funcionários ativos", value: stats.ativos },
                            { label: "Motoristas", value: stats.motoristas },
                            { label: "Auxiliares", value: stats.auxiliares },
                        ].map(item => (
                            <div key={item.label} className="flex justify-between">
                                <span className="text-muted-foreground">{item.label}</span>
                                <span className={item.bold ? "font-bold text-teal-700" : "font-medium text-foreground"}>{item.value}</span>
                            </div>
                        ))}
                    </div>
                </div>

                
            </div>
                        {/* Metas não atingidas */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <MetaNaoAtingidaCard
                    title="Não atingiram Conclusão"
                    subtitle={`Meta: ${metas.conclusao}% ou mais`}
                    icon="fa fa-check-circle"
                    lista={stats.naoAtingiuConclusao}
                    onFuncionarioSelected={onFuncionarioSelected}
                    renderValor={c => formatPercentValue(getPercentualConclusao(c))}
                />
                <MetaNaoAtingidaCard
                    title="Não atingiram Telemetria"
                    subtitle={`Meta: ${metas.telemetria}% ou mais`}
                    icon="fa fa-shield"
                    lista={stats.naoAtingiuTelemetria}
                    onFuncionarioSelected={onFuncionarioSelected}
                    renderValor={c => formatPercentValue(getMediaTelemetria(c))}
                />
                <MetaNaoAtingidaCard
                    title="Não atingiram Retorno"
                    subtitle={`Meta: até ${metas.retorno}%`}
                    icon="fa fa-repeat"
                    lista={stats.naoAtingiuRetorno}
                    onFuncionarioSelected={onFuncionarioSelected}
                    renderValor={c => formatPercentValue(getPercentualRetorno(c))}
                />
            </div>

        </motion.div>
    );
}

export default DashboardTab;

// ─── StatCard ─────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, sub, color = "teal" }) {
    const colors = {
        teal: "bg-teal-50 border-teal-200 text-teal-700",
        blue: "bg-blue-50 border-blue-200 text-blue-700",
        amber: "bg-amber-50 border-amber-200 text-amber-700",
        rose: "bg-rose-50 border-rose-200 text-rose-700",
        violet: "bg-violet-50 border-violet-200 text-violet-700",
        gray: "bg-muted border-border text-muted-foreground",
    };
    return (
        <div className={`rounded-xl border p-4 flex items-start gap-3 transition-colors duration-150 ${colors[color]}`}>
            <div className="text-xl mt-0.5 w-5 flex-shrink-0 text-center">
                <i className={icon} />
            </div>
            <div>
                <p className="text-xs font-medium opacity-70">{label}</p>
                <p className="text-lg font-bold leading-tight">{value}</p>
                {sub && <p className="text-xs opacity-60 mt-0.5">{sub}</p>}
            </div>
        </div>
    );
}

// ─── Meta não atingida (lista clicável) ────────────────────────────────────────
function formatPercentValue(value) {
    return value != null ? `${Number(value).toFixed(1)}%` : "—";
}

function MetaNaoAtingidaCard({ title, subtitle, icon, lista, onFuncionarioSelected, renderValor }) {
    return (
        <div className="rounded-xl border border-border bg-card p-4 shadow-card">
            <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold text-sm flex items-center gap-2 text-foreground">
                    <i className={`${icon} text-rose-600`} /> {title}
                </h3>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">
                    {lista.length}
                </span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">{subtitle}</p>

            {lista.length === 0 ? (
                <p className="text-xs text-muted-foreground py-4 text-center opacity-70">
                    Todos atingiram a meta 🎉
                </p>
            ) : (
                <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                    {lista.map(c => (
                        <button
                            key={c.id ?? c.funcionarioId}
                            onClick={() => onFuncionarioSelected?.(c)}
                            className="w-full flex items-center gap-2 text-xs bg-background hover:bg-muted border border-border rounded-lg px-3 py-2 transition-colors duration-150 text-left"
                        >
                            <div className="flex-1 min-w-0">
                                <p className="truncate font-medium text-foreground">{c.funcionarioNome}</p>
                                <p className="text-muted-foreground truncate">{c.filialNome} · {c.funcionarioCodigo}</p>
                            </div>
                            <span className="font-bold text-rose-600 flex-shrink-0">
                                {renderValor(c)}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Mini bar chart (pure CSS) ────────────────────────────────────────────────
function MiniBar({ label, value, max, color = "#14b8a6", suffix = "" }) {
    const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
    return (
        <div className="flex items-center gap-2 text-xs">
            <span className="w-28 truncate text-muted-foreground flex-shrink-0">{label}</span>
            <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                <div
                    className="h-2 rounded-full transition-all duration-500"
                    style={{ width: `${pct}%`, background: color }}
                />
            </div>
            <span className="w-20 text-right font-semibold text-foreground">
                {typeof value === "number" ? value.toLocaleString("pt-BR", { maximumFractionDigits: 2 }) : value}{suffix}
            </span>
        </div>
    );
}
